import { formatPhoneNumber } from '@application/utils';
import type {
  AccountUpdateInput,
  AddressUncheckedCreateWithoutAccountInput,
  AddressUncheckedUpdateWithoutAccountInput,
  DogBreederBreedsCreateWithoutDogBreederInput,
  DogBreederTranslationUpsertWithWhereUniqueWithoutDogBreederInput,
  DogBreederUncheckedUpdateWithoutAccountInput,
  GenderEnum,
  MtBreederOnboardingGetProfileQuery,
} from '@gql/graphql';
import type { MtProfileBreederValues } from '@presentation/pages';
import cleanDeep from 'clean-deep';
import { useCallback } from 'react';

export const useProfileUpdateBuilder = () => {
  const profileUpdateBuilder = useCallback(
    (
      values: MtProfileBreederValues,
      profile?: MtBreederOnboardingGetProfileQuery['accountMe'] | null,
    ): { data: AccountUpdateInput } => {
      const phone =
        values.phone && values.phonePrefixCountry
          ? formatPhoneNumber(values.phone, values.phonePrefixCountry)
          : values.phone;

      const rootAccountUpdate: AccountUpdateInput = {
        firstName: { set: values.firstName },
        lastName: { set: values.lastName },
        ...(values?.birthday && { birthday: { set: values.birthday?.toISOString() } }),
        ...(values?.gender && { gender: { set: values.gender as GenderEnum } }),
        phone: { set: phone },
      };

      const addressUpdate: AddressUncheckedUpdateWithoutAccountInput = {
        city: { set: values.city },
        zip: { set: values.zip },
        country: { set: values.country },
        region: { set: values.region },
      };

      const addressCreate: AddressUncheckedCreateWithoutAccountInput = {
        city: values.city,
        zip: values.zip,
        country: values.country,
        region: values.region,
      };

      const breederUpdate: DogBreederUncheckedUpdateWithoutAccountInput = {
        displayName: { set: values.displayName },
        breedingName: { set: values.breedingName },
        displayEmail: { set: values.email },
        displayPhone: { set: phone },
        level: { set: values.level },
        websiteUrl: { set: values.websiteUrl },
        facebookUrl: { set: values.facebookUrl },
        agreementNumber: { set: values.agreementNumber },
        spokenLanguages: values.spokenLanguages,
        dogsPedigree: { set: values.dogsPedigree ?? null },
        motherRestBetweenLitters: { set: values.motherRestBetweenLitters },
        littersByFemale: { set: values.littersByFemale },
        dogsConsanguinity: { set: values.dogsConsanguinity },
        medicalTestsPerformed: { set: values.medicalTestsPerformed },
        veterinaryInfo: { set: values.veterinaryInfo },
      };

      const breederBreedsUpdate = {
        deleteMany: [{ dogBreederId: { equals: profile?.dogBreeder?.id } }],
        create: (values.dogBreedsIds ?? []).map(
          (breedId) =>
            ({
              dogBreed: {
                connect: { id: breedId },
              },
            } as DogBreederBreedsCreateWithoutDogBreederInput),
        ),
      };

      const translationUpsert = Object.keys(values.translated ?? {}).map(
        (language) =>
          ({
            where: { id: values.translated?.[language]?.id ?? -1 },
            create: {
              language,
              ...cleanDeep(
                {
                  description: values.translated?.[language]?.description,
                  dogsLivingPlace: values.translated?.[language]?.dogsLivingPlace,
                  breederDogsSelection: values.translated?.[language]?.breederDogsSelection,
                  puppiesTraining: values.translated?.[language]?.puppiesTraining,
                  puppiesLivingPlace: values.translated?.[language]?.puppiesLivingPlace,
                },
                { nullValues: false },
              ),
            },
            update: cleanDeep(
              {
                language: { set: language },
                description: { set: values.translated?.[language]?.description },
                dogsLivingPlace: { set: values.translated?.[language]?.dogsLivingPlace },
                breederDogsSelection: { set: values.translated?.[language]?.breederDogsSelection },
                puppiesTraining: { set: values.translated?.[language]?.puppiesTraining },
                puppiesLivingPlace: { set: values.translated?.[language]?.puppiesLivingPlace },
              },
              { nullValues: false },
            ),
          } as DogBreederTranslationUpsertWithWhereUniqueWithoutDogBreederInput),
      );

      const data: AccountUpdateInput = {
        ...cleanDeep(rootAccountUpdate, { nullValues: false }),
        address: {
          upsert: {
            create: cleanDeep(addressCreate),
            update: cleanDeep(addressUpdate, { nullValues: false }),
          },
        },
        dogBreeder: {
          update: {
            data: {
              ...cleanDeep(breederUpdate, { nullValues: false }),
              ...(!values.dogBreedsIds?.length ? {} : { breederBreeds: breederBreedsUpdate }),
              ...(!Object.keys(values.translated ?? {})?.length
                ? {}
                : { translations: { upsert: translationUpsert } }),
            },
          },
        },
      };

      return { data };
    },
    [],
  );

  return { profileUpdateBuilder };
};
